// components/sections/GapAnalysisFilters.js - Avec dropdowns personnalisés fond blanc
import { ChevronDown, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const GapAnalysisFilters = ({
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    filterBy,
    setFilterBy,
    categoryFilter,
    setCategoryFilter,
    priorityFilter, // Filtre par priorité
    setPriorityFilter, // Setter pour priorité
    onReset,
    showOnlySort = false,
    showOnlyFilter = false
}) => {
    // États pour les dropdowns ouverts
    const [openDropdown, setOpenDropdown] = useState(null);
    
    // Refs pour fermer les dropdowns en cliquant à l'extérieur
    const sortByRef = useRef(null);
    const sortOrderRef = useRef(null);
    const categoryRef = useRef(null);
    const priorityRef = useRef(null);
    const confidenceRef = useRef(null);

    const sortOptions = [
        { value: 'category', label: 'Catégorie' },
        { value: 'confidence', label: 'Confiance' },
        { value: 'progress', label: 'Progression' },
        { value: 'timeline', label: 'Échéance' },
        { value: 'priority', label: 'Priorité' }
    ];

    const sortOrderOptions = [
        { value: 'asc', label: 'Croissant' },
        { value: 'desc', label: 'Décroissant' }
    ];

    const confidenceFilterOptions = [
        { value: 'all', label: 'Toutes les confiances' },
        { value: 'high', label: 'Confiance élevée (80%+)' },
        { value: 'medium', label: 'Confiance moyenne (60-79%)' },
        { value: 'low', label: 'Confiance faible (<60%)' }
    ];

    const categoryOptions = [
        { value: 'all', label: 'Toutes les catégories' },
        { value: 'patrimoine-immobilier', label: 'Patrimoine Immobilier' },
        { value: 'revenus-locatifs', label: 'Cash Flow Immobilier' },
        { value: 'strategies-immobilieres', label: 'Compétences & Stratégies' },
        { value: 'reseau-visibilite', label: 'Réseau & Visibilité' },
        { value: 'autres', label: 'Autres' }
    ];

    // Options de filtre par priorité
    const priorityOptions = [
        { value: 'all', label: 'Toutes les priorités', color: 'text-gray-600' },
        { value: 'high', label: 'Priorité haute', color: 'text-red-600' },
        { value: 'medium', label: 'Priorité moyenne', color: 'text-blue-600' },
        { value: 'low', label: 'Priorité basse', color: 'text-gray-600' }
    ];

    // Fermer les dropdowns en cliquant à l'extérieur
    useEffect(() => {
        const handleClickOutside = (event) => {
            const refs = [sortByRef, sortOrderRef, categoryRef, priorityRef, confidenceRef];
            const clickedOutside = refs.every(ref => 
                ref.current && !ref.current.contains(event.target)
            );
            
            if (clickedOutside) {
                setOpenDropdown(null);
            }
        };

        if (openDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [openDropdown]);

    const hasActiveFilters = () => {
        return filterBy !== 'all' ||
            categoryFilter !== 'all' ||
            (priorityFilter && priorityFilter !== 'all') ||
            sortBy !== 'category' ||
            sortOrder !== 'asc';
    };

    // Composant Dropdown personnalisé
    const CustomDropdown = ({ 
        value, 
        onChange, 
        options, 
        placeholder, 
        dropdownKey, 
        dropdownRef 
    }) => {
        const isOpen = openDropdown === dropdownKey;
        const selectedOption = options.find(opt => opt.value === value);

        const handleToggle = () => {
            setOpenDropdown(isOpen ? null : dropdownKey);
        };

        const handleSelect = (optionValue) => {
            onChange(optionValue);
            setOpenDropdown(null);
        };

        return (
            <div className="relative" ref={dropdownRef}>
                <button
                    type="button"
                    onClick={handleToggle}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 bg-white hover:border-gray-400 text-left flex items-center justify-between min-w-[120px]"
                >
                    <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
                        {selectedOption ? selectedOption.label : placeholder}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown */}
                {isOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-64 overflow-hidden">
                        {/* Liste des options */}
                        <div className="max-h-64 overflow-y-auto">
                            {options.map((option, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleSelect(option.value)}
                                    className={`w-full px-4 py-2 text-left text-sm hover:bg-green-50 transition-colors duration-100 ${
                                        value === option.value
                                            ? 'bg-green-100 text-green-800 font-medium'
                                            : 'text-gray-700 hover:text-green-700'
                                    }`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
            <div className="flex flex-wrap items-center gap-4">
                {/* Options de tri */}
                {(showOnlySort || !showOnlyFilter) && (
                    <>
                        <div className="flex items-center space-x-2">
                            <label className="text-sm font-medium text-gray-700">Trier par :</label>
                            <CustomDropdown
                                value={sortBy}
                                onChange={setSortBy}
                                options={sortOptions}
                                placeholder="Sélectionner..."
                                dropdownKey="sortBy"
                                dropdownRef={sortByRef}
                            />
                        </div>

                        <div className="flex items-center space-x-2">
                            <label className="text-sm font-medium text-gray-700">Ordre :</label>
                            <CustomDropdown
                                value={sortOrder}
                                onChange={setSortOrder}
                                options={sortOrderOptions}
                                placeholder="Sélectionner..."
                                dropdownKey="sortOrder"
                                dropdownRef={sortOrderRef}
                            />
                        </div>
                    </>
                )}

                {/* Séparateur */}
                {!showOnlySort && !showOnlyFilter && (
                    <div className="h-6 w-px bg-gray-300"></div>
                )}

                {/* Options de filtrage */}
                {(showOnlyFilter || !showOnlySort) && (
                    <>
                        <div className="flex items-center space-x-2">
                            <label className="text-sm font-medium text-gray-700">Catégorie :</label>
                            <CustomDropdown
                                value={categoryFilter}
                                onChange={setCategoryFilter}
                                options={categoryOptions}
                                placeholder="Sélectionner..."
                                dropdownKey="category"
                                dropdownRef={categoryRef}
                            />
                        </div>

                        {/* Filtre par priorité */}
                        {priorityFilter !== undefined && (
                            <div className="flex items-center space-x-2">
                                <label className="text-sm font-medium text-gray-700">Priorité :</label>
                                <CustomDropdown
                                    value={priorityFilter}
                                    onChange={setPriorityFilter}
                                    options={priorityOptions}
                                    placeholder="Sélectionner..."
                                    dropdownKey="priority"
                                    dropdownRef={priorityRef}
                                />
                            </div>
                        )}

                        <div className="flex items-center space-x-2">
                            <label className="text-sm font-medium text-gray-700">Confiance :</label>
                            <CustomDropdown
                                value={filterBy}
                                onChange={setFilterBy}
                                options={confidenceFilterOptions}
                                placeholder="Sélectionner..."
                                dropdownKey="confidence"
                                dropdownRef={confidenceRef}
                            />
                        </div>
                    </>
                )}

                {/* Bouton de réinitialisation */}
                {hasActiveFilters() && (
                    <>
                        <div className="h-6 w-px bg-gray-300"></div>
                        <button
                            onClick={onReset}
                            className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                        >
                            <RotateCcw className="w-4 h-4" />
                            <span>Réinitialiser</span>
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default GapAnalysisFilters;